<template>
  <transition name="fade" mode="out-in">
    <base-modal v-if="visible" :title="modalTitle" height="85vh" @close="$emit('close')">
      <div class="em-dd flex flex-col h-full min-h-0 px-8 pt-4">
        <div
          v-if="!loading && hasRows"
          class="em-dd-toolbar flex items-center justify-between gap-3 mb-3"
        >
          <p
            v-if="inlineNotice"
            class="em-dd-toolbar__notice m-0"
            :class="inlineNotice.type === 'success' ? 'em-dd-toolbar__notice--success' : 'em-dd-toolbar__notice--error'"
          >
            <font-awesome-icon
              :icon="['fas', inlineNotice.type === 'success' ? 'check-circle' : 'exclamation-circle']"
              class="em-dd-toolbar__notice-icon"
            />
            <span>{{ inlineNotice.text }}</span>
          </p>
          <div v-else class="em-dd-toolbar__spacer"></div>
          <button type="button" class="em-dd-export-btn" @click.stop="exportExcel">
            <font-awesome-icon :icon="['fas', 'file-excel']" class="em-dd-export-btn__icon" />
            <span>Export Excel</span>
          </button>
        </div>

        <div
          v-else-if="inlineNotice"
          class="em-dd-toolbar flex items-center mb-3"
        >
          <p
            class="em-dd-toolbar__notice m-0"
            :class="inlineNotice.type === 'success' ? 'em-dd-toolbar__notice--success' : 'em-dd-toolbar__notice--error'"
          >
            <font-awesome-icon
              :icon="['fas', inlineNotice.type === 'success' ? 'check-circle' : 'exclamation-circle']"
              class="em-dd-toolbar__notice-icon"
            />
            <span>{{ inlineNotice.text }}</span>
          </p>
        </div>

        <div v-if="loading" class="flex flex-1 min-h-0 items-center justify-center py-16">
          <base-spinner></base-spinner>
        </div>

        <div v-else-if="hasRows" class="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div class="overflow-x-auto overflow-y-auto flex-1 border rounded-lg">
            <table class="min-w-full text-left text-sm">
              <thead class="em-dd-head border-b sticky top-0">
                <tr>
                  <th class="px-4 py-2 text-gray-600">#</th>
                  <th class="px-4 py-2 text-gray-600">Name</th>
                  <th class="px-4 py-2 text-gray-600">Student ID</th>
                  <th class="px-4 py-2 text-gray-600">EID</th>
                  <th class="px-4 py-2 text-gray-600">Email</th>
                  <th class="px-4 py-2 text-gray-600">Phone</th>
                  <th class="px-4 py-2 text-gray-600">Entity</th>
                  <th class="px-4 py-2 text-gray-600">Campus</th>
                  <th class="px-4 py-2 text-gray-600">Program</th>
                  <th class="px-4 py-2 text-gray-600">Academic year</th>
                  <th class="px-4 py-2 text-gray-600">Current status</th>
                  <th class="px-4 py-2 text-gray-600">Employer</th>
                  <th class="px-4 py-2 text-gray-600">Position</th>
                  <th class="px-4 py-2 text-gray-600">Location</th>
                  <th class="px-4 py-2 text-gray-600">Employment Source</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, idx) in students"
                  :key="row.student_id + '-em-dd-' + idx"
                  class="border-b border-gray-100"
                >
                  <td class="px-4 py-2 whitespace-nowrap">{{ firstItem + idx }}</td>
                  <td class="px-4 py-2 whitespace-nowrap">{{ row.name || "—" }}</td>
                  <td class="px-4 py-2 whitespace-nowrap">{{ row.student_id || "—" }}</td>
                  <td class="px-4 py-2 whitespace-nowrap">{{ row.eid || "—" }}</td>
                  <td class="px-4 py-2">{{ row.email || "—" }}</td>
                  <td class="px-4 py-2 whitespace-nowrap">{{ row.phone || "—" }}</td>
                  <td class="px-4 py-2 whitespace-nowrap">{{ row.entity || "—" }}</td>
                  <td class="px-4 py-2 whitespace-nowrap">{{ row.campus || "—" }}</td>
                  <td class="px-4 py-2">{{ row.program || "—" }}</td>
                  <td class="px-4 py-2 whitespace-nowrap">{{ row.academic_year || "—" }}</td>
                  <td class="px-4 py-2 whitespace-nowrap">{{ row.current_status || "—" }}</td>
                  <td class="px-4 py-2">{{ row.employer || "—" }}</td>
                  <td class="px-4 py-2">
                    <div
                      v-if="isEditing(row, 'position')"
                      class="em-dd-edit-wrap"
                    >
                      <input
                        :ref="'editInput-' + row.student_id + '-position'"
                        v-model="editValue"
                        type="text"
                        class="em-dd-edit-input"
                        :disabled="savingEdit"
                        @keydown.enter.prevent="commitEditFromKeyboard"
                        @keydown.esc.prevent="cancelEdit(true)"
                        @blur="commitEditFromBlur"
                      />
                    </div>
                    <button
                      v-else-if="canEdit"
                      type="button"
                      class="em-dd-editable-btn"
                      title="Click to edit position"
                      @click="startEdit(row, 'position')"
                    >
                      <span class="em-dd-editable-btn__text">{{ row.position || "—" }}</span>
                      <font-awesome-icon :icon="['fas', 'pen']" class="em-dd-editable-btn__icon" />
                    </button>
                    <span v-else>{{ row.position || "—" }}</span>
                  </td>
                  <td class="px-4 py-2">
                    <div
                      v-if="isEditing(row, 'location')"
                      class="em-dd-edit-wrap"
                    >
                      <input
                        :ref="'editInput-' + row.student_id + '-location'"
                        v-model="editValue"
                        type="text"
                        class="em-dd-edit-input"
                        :disabled="savingEdit"
                        @keydown.enter.prevent="commitEditFromKeyboard"
                        @keydown.esc.prevent="cancelEdit(true)"
                        @blur="commitEditFromBlur"
                      />
                    </div>
                    <button
                      v-else-if="canEdit"
                      type="button"
                      class="em-dd-editable-btn"
                      title="Click to edit location"
                      @click="startEdit(row, 'location')"
                    >
                      <span class="em-dd-editable-btn__text">{{ row.location || "—" }}</span>
                      <font-awesome-icon :icon="['fas', 'pen']" class="em-dd-editable-btn__icon" />
                    </button>
                    <span v-else>{{ row.location || "—" }}</span>
                  </td>
                  <td class="px-4 py-2">{{ row.employment_source || "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="mt-3 shrink-0">
            <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
          </div>
        </div>

        <div v-else class="flex flex-1 min-h-0 items-center justify-center py-16 text-gray-500">
          No students found for this employer.
        </div>
      </div>
    </base-modal>
  </transition>
</template>

<script>
import gql from "graphql-tag";
import xlsx from "xlsx/dist/xlsx.full.min.js";
import Pagination from "../../components/shared/pagination.vue";

var STUDENTS_QUERY = gql`
  query ManagedEmployerStudents($employer: String!, $first: Int!, $page: Int!) {
    managedEmployerStudents(employer: $employer, first: $first, page: $page) {
      error
      error_code
      message
      data {
        students {
          name
          student_id
          eid
          email
          phone
          entity
          campus
          program
          academic_year
          current_status
          employer
          position
          location
          employment_source
        }
        paginatorInfo {
          count
          currentPage
          firstItem
          hasMorePages
          lastItem
          lastPage
          perPage
          total
        }
      }
    }
  }
`;

var UPDATE_EMPLOYMENT_MUTATION = gql`
  mutation UpdateManagedEmployerStudentEmployment(
    $student_id: String!
    $field: String!
    $value: String
  ) {
    updateManagedEmployerStudentEmployment(
      student_id: $student_id
      field: $field
      value: $value
    ) {
      error
      error_code
      message
      data {
        student_id
        field
        value
        students_updated
        employment_informations_updated
      }
    }
  }
`;

var EXPORT_COLUMNS = [
  { key: "name", header: "NAME" },
  { key: "student_id", header: "STUDENT ID" },
  { key: "eid", header: "EID" },
  { key: "email", header: "EMAIL" },
  { key: "phone", header: "PHONE" },
  { key: "entity", header: "ENTITY" },
  { key: "campus", header: "CAMPUS" },
  { key: "program", header: "PROGRAM" },
  { key: "academic_year", header: "ACADEMIC YEAR" },
  { key: "current_status", header: "CURRENT STATUS" },
  { key: "employer", header: "EMPLOYER" },
  { key: "position", header: "POSITION" },
  { key: "location", header: "LOCATION" },
  { key: "employment_source", header: "EMPLOYMENT SOURCE" },
];

function mapExportRow(obj) {
  return {
    name: obj.name || "",
    student_id: obj.student_id || "",
    eid: obj.eid || "",
    email: obj.email || "",
    phone: obj.phone || "",
    entity: obj.entity || "",
    campus: obj.campus || "",
    program: obj.program || "",
    academic_year: obj.academic_year || "",
    current_status: obj.current_status || "",
    employer: obj.employer || "",
    position: obj.position || "",
    location: obj.location || "",
    employment_source: obj.employment_source || "",
  };
}

function runDetachedEmployerStudentsExport(ctx) {
  var store = ctx.store;
  var apolloClient = ctx.apolloClient;
  var jobId = ctx.jobId;
  var fileName = ctx.fileName;
  var employer = ctx.employer;
  var sheetLabel = ctx.sheetLabel;

  var allRows = [];
  var page = 1;
  var limit = 200;

  function fetchNext() {
    apolloClient
      .query({
        query: STUDENTS_QUERY,
        fetchPolicy: "network-only",
        variables: {
          employer: employer,
          page: page,
          first: limit,
        },
      })
      .then(function (result) {
        var payload =
          result && result.data && result.data.managedEmployerStudents
            ? result.data.managedEmployerStudents
            : null;

        if (!payload || payload.error) {
          throw new Error(
            (payload && payload.message) ? payload.message : "Server error"
          );
        }

        var data = payload.data || {};
        var batch = data.students || [];
        var paginator = data.paginatorInfo || {};

        if (batch.length > 0) {
          for (var i = 0; i < batch.length; i++) {
            allRows.push(mapExportRow(batch[i]));
          }

          var total = paginator.total || allRows.length;
          var percent = total > 0
            ? Math.min(95, Math.round((allRows.length / total) * 100))
            : Math.min(95, Math.round(
              (allRows.length / Math.max(allRows.length + limit, 1)) * 100
            ));

          store.dispatch("exportManager/updateJob", {
            id: jobId,
            progress: percent,
            currentSheet: sheetLabel + " (" + allRows.length + " rows)",
          });
        }

        if (paginator.hasMorePages) {
          page = page + 1;
          fetchNext();
        } else {
          finishExport();
        }
      })
      .catch(function (error) {
        if (allRows.length > 0) {
          finishExport();
        } else {
          store.dispatch("exportManager/failJob", {
            id: jobId,
            error: (error && error.message) ? error.message : "Export failed",
          });
        }
      });
  }

  function finishExport() {
    try {
      if (allRows.length === 0) {
        store.dispatch("exportManager/failJob", {
          id: jobId,
          error: "No data available for export",
        });
        return;
      }

      var colKeys = EXPORT_COLUMNS.map(function (col) {
        return col.key;
      });

      var rows = allRows.map(function (row) {
        var out = {};
        for (var i = 0; i < EXPORT_COLUMNS.length; i++) {
          var key = EXPORT_COLUMNS[i].key;
          out[key] = row[key] !== undefined && row[key] !== null ? row[key] : "";
        }
        return out;
      });

      var worksheet = xlsx.utils.json_to_sheet(rows, { header: colKeys });

      var columnWidths = [];
      for (var c = 0; c < EXPORT_COLUMNS.length; c++) {
        var column = String.fromCharCode(65 + (c % 26));
        var r = Math.floor(c / 26);
        var columnLetter = r > 0 ? String.fromCharCode(64 + r) + column : column;
        worksheet[columnLetter + "1"] = { v: EXPORT_COLUMNS[c].header, t: "s" };
        columnWidths.push({ width: Math.max(16, EXPORT_COLUMNS[c].header.length + 5) });
      }

      worksheet["!cols"] = columnWidths;

      var workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, "Students");
      xlsx.writeFile(workbook, fileName);

      store.dispatch("exportManager/completeJob", {
        id: jobId,
        rowCount: allRows.length,
      });
    } catch (error) {
      store.dispatch("exportManager/failJob", {
        id: jobId,
        error: (error && error.message) ? error.message : "Export failed",
      });
    }
  }

  fetchNext();
}

export default {
  name: "EmployerStudentsDrilldownModal",
  components: {
    Pagination: Pagination,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    employerName: {
      type: String,
      default: "",
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close"],
  data: function () {
    return {
      loading: false,
      students: [],
      paginatorInfoData: null,
      pageSize: 10,
      editingStudentId: null,
      editingField: null,
      editValue: "",
      editOriginalValue: "",
      savingEdit: false,
      suppressBlurSave: false,
      inlineNotice: null,
      inlineNoticeTimer: null,
    };
  },
  beforeUnmount: function () {
    this.clearInlineNoticeTimer();
  },
  computed: {
    modalTitle: function () {
      if (this.employerName) {
        return this.employerName + " — Students";
      }
      return "Students";
    },
    hasRows: function () {
      if (this.paginatorInfoData && this.paginatorInfoData.total > 0) {
        return true;
      }
      return this.students && this.students.length > 0;
    },
    paginatorInfo: function () {
      if (this.paginatorInfoData) {
        return this.paginatorInfoData;
      }
      return {
        count: 0,
        currentPage: 1,
        firstItem: 0,
        hasMorePages: false,
        lastItem: 0,
        lastPage: 1,
        perPage: this.pageSize,
        total: 0,
      };
    },
    firstItem: function () {
      var info = this.paginatorInfo;
      return info && info.firstItem ? info.firstItem : 1;
    },
  },
  watch: {
    visible: function (isVisible) {
      if (isVisible) {
        this.fetchStudents(1);
      } else {
        this.students = [];
        this.paginatorInfoData = null;
        this.cancelEdit(true);
        this.clearInlineNotice();
      }
    },
    employerName: function () {
      if (this.visible) {
        this.cancelEdit(true);
        this.clearInlineNotice();
        this.fetchStudents(1);
      }
    },
  },
  methods: {
    clearInlineNoticeTimer: function () {
      if (this.inlineNoticeTimer) {
        clearTimeout(this.inlineNoticeTimer);
        this.inlineNoticeTimer = null;
      }
    },
    clearInlineNotice: function () {
      this.clearInlineNoticeTimer();
      this.inlineNotice = null;
    },
    showInlineNotice: function (type, text) {
      var self = this;
      this.clearInlineNoticeTimer();
      this.inlineNotice = {
        type: type === "error" ? "error" : "success",
        text: text || "",
      };
      this.inlineNoticeTimer = setTimeout(function () {
        self.inlineNotice = null;
        self.inlineNoticeTimer = null;
      }, type === "error" ? 4000 : 2500);
    },
    setPage: function (page) {
      this.cancelEdit(true);
      this.fetchStudents(page);
    },
    isEditing: function (row, field) {
      if (!row || !row.student_id) {
        return false;
      }
      return this.editingStudentId === row.student_id && this.editingField === field;
    },
    startEdit: function (row, field) {
      if (!this.canEdit || !row || !row.student_id) {
        return;
      }
      if (field !== "position" && field !== "location") {
        return;
      }
      if (this.savingEdit) {
        return;
      }
      if (this.isEditing(row, field)) {
        return;
      }
      if (this.editingStudentId && (this.editingStudentId !== row.student_id || this.editingField !== field)) {
        this.cancelEdit(true);
      }

      var current = row[field] || "";
      this.editingStudentId = row.student_id;
      this.editingField = field;
      this.editValue = current;
      this.editOriginalValue = current;

      var self = this;
      this.$nextTick(function () {
        var refName = "editInput-" + row.student_id + "-" + field;
        var input = self.$refs[refName];
        if (Array.isArray(input)) {
          input = input[0];
        }
        if (input && input.focus) {
          input.focus();
          if (input.select) {
            input.select();
          }
        }
      });
    },
    cancelEdit: function (force) {
      if (this.savingEdit && !force) {
        return;
      }
      this.suppressBlurSave = true;
      this.editingStudentId = null;
      this.editingField = null;
      this.editValue = "";
      this.editOriginalValue = "";
      this.savingEdit = false;
      var self = this;
      this.$nextTick(function () {
        self.suppressBlurSave = false;
      });
    },
    commitEditFromKeyboard: function () {
      this.suppressBlurSave = true;
      this.saveEdit();
    },
    commitEditFromBlur: function () {
      if (this.suppressBlurSave || this.savingEdit) {
        return;
      }
      this.saveEdit();
    },
    applyLocalFieldUpdate: function (studentId, field, savedValue) {
      var nextRows = [];
      for (var i = 0; i < this.students.length; i++) {
        var row = this.students[i];
        if (row && row.student_id === studentId) {
          var patched = Object.assign({}, row);
          patched[field] = savedValue;
          nextRows.push(patched);
        } else {
          nextRows.push(row);
        }
      }
      this.students = nextRows;
    },
    async saveEdit() {
      if (!this.canEdit || this.savingEdit) {
        return;
      }
      if (!this.editingStudentId || !this.editingField) {
        return;
      }

      var studentId = this.editingStudentId;
      var field = this.editingField;
      var nextValue = (this.editValue || "").trim();
      var prevValue = (this.editOriginalValue || "").trim();
      var fieldLabel = field === "location" ? "Location" : "Position";

      if (nextValue === prevValue) {
        this.cancelEdit(true);
        return;
      }

      this.savingEdit = true;
      this.suppressBlurSave = true;
      try {
        var result = await this.$apollo.mutate({
          mutation: UPDATE_EMPLOYMENT_MUTATION,
          variables: {
            student_id: studentId,
            field: field,
            value: nextValue,
          },
        });
        var payload =
          result && result.data && result.data.updateManagedEmployerStudentEmployment
            ? result.data.updateManagedEmployerStudentEmployment
            : null;

        if (!payload) {
          throw new Error("No response");
        }

        if (payload.error) {
          this.savingEdit = false;
          this.cancelEdit(true);
          await this.$nextTick();
          this.showInlineNotice(
            "error",
            payload.message || fieldLabel + " could not be saved."
          );
          return;
        }

        var savedValue =
          payload.data && payload.data.value != null ? payload.data.value : nextValue;
        this.applyLocalFieldUpdate(studentId, field, savedValue);
        this.savingEdit = false;
        this.cancelEdit(true);
        await this.$nextTick();
        this.showInlineNotice("success", fieldLabel + " updated successfully.");
      } catch (err) {
        var partial =
          err && err.data && err.data.updateManagedEmployerStudentEmployment
            ? err.data.updateManagedEmployerStudentEmployment
            : null;
        if (partial && !partial.error) {
          var recoveredValue =
            partial.data && partial.data.value != null ? partial.data.value : nextValue;
          this.applyLocalFieldUpdate(studentId, field, recoveredValue);
          this.savingEdit = false;
          this.cancelEdit(true);
          await this.$nextTick();
          this.showInlineNotice("success", fieldLabel + " updated successfully.");
          return;
        }

        console.error("Failed to update employer student field:", err);
        this.savingEdit = false;
        this.cancelEdit(true);
        await this.$nextTick();
        this.showInlineNotice("error", fieldLabel + " could not be updated.");
      }
    },
    async fetchStudents(pageNo) {
      if (!this.employerName) {
        this.students = [];
        this.paginatorInfoData = null;
        return;
      }

      this.loading = true;
      try {
        var response = await this.$apollo.query({
          query: STUDENTS_QUERY,
          variables: {
            employer: this.employerName,
            page: pageNo || 1,
            first: this.pageSize,
          },
          fetchPolicy: "network-only",
        });

        var payload =
          response.data && response.data.managedEmployerStudents
            ? response.data.managedEmployerStudents
            : null;

        if (!payload || payload.error) {
          this.students = [];
          this.paginatorInfoData = null;
          return;
        }

        var data = payload.data || {};
        this.students = data.students || [];
        this.paginatorInfoData = data.paginatorInfo || null;
      } catch (error) {
        console.error("Failed to load employer students:", error);
        this.students = [];
        this.paginatorInfoData = null;
      }
      this.loading = false;
    },
    exportExcel: function () {
      if (!this.employerName) {
        return;
      }

      var store = this.$store;
      var apolloClient = this.$apollo && this.$apollo.provider
        ? this.$apollo.provider.defaultClient
        : null;
      if (!apolloClient) {
        return;
      }

      var employerName = this.employerName;
      var safeName = String(employerName || "Employer")
        .replace(/[^\w-]+/g, "_")
        .substring(0, 60);
      var fileName = "Employer_Students_" + safeName + ".xlsx";
      var sheetLabel = this.modalTitle;

      store
        .dispatch("exportManager/addJob", {
          fileName: fileName,
          totalSheets: 1,
        })
        .then(function (jobId) {
          store.dispatch("exportManager/setMinimized", false);
          store.dispatch("exportManager/updateJob", {
            id: jobId,
            progress: 5,
            currentSheet: sheetLabel,
          });

          runDetachedEmployerStudentsExport({
            store: store,
            apolloClient: apolloClient,
            jobId: jobId,
            fileName: fileName,
            employer: employerName,
            sheetLabel: sheetLabel,
          });
        });
    },
  },
};
</script>

<style scoped>
.em-dd-toolbar__spacer {
  flex: 1;
  min-width: 0;
}
.em-dd-toolbar__notice {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  flex: 1;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.3;
}
.em-dd-toolbar__notice-icon {
  font-size: 0.875rem;
  flex-shrink: 0;
}
.em-dd-toolbar__notice--success {
  color: #047857;
}
.em-dd-toolbar__notice--error {
  color: #b91c1c;
}

.em-dd-head {
  background: #f8fafc;
}

.em-dd-editable-btn {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  min-width: 9rem;
  max-width: 16rem;
  margin: 0;
  padding: 0.35rem 0.55rem;
  border: 1px solid transparent;
  border-radius: 0.375rem;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.8125rem;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, color 0.15s ease;
}
.em-dd-editable-btn__icon {
  flex-shrink: 0;
  font-size: 0.7rem;
  color: #2563eb;
  opacity: 0;
  transition: opacity 0.15s ease;
}
.em-dd-editable-btn:hover,
.em-dd-editable-btn:focus {
  border-color: #93c5fd;
  border-style: dashed;
  background: #eff6ff;
  color: #1e3a8a;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  outline: none;
}
.em-dd-editable-btn:hover .em-dd-editable-btn__icon,
.em-dd-editable-btn:focus .em-dd-editable-btn__icon {
  opacity: 0.85;
}
.em-dd-editable-btn__text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.em-dd-edit-wrap {
  min-width: 9rem;
  max-width: 16rem;
}

.em-dd-edit-input {
  width: 100%;
  min-width: 9rem;
  padding: 0.35rem 0.5rem;
  border: 1px solid #3b82f6;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  background: #fff;
}
.em-dd-edit-input:disabled {
  opacity: 0.7;
}

.em-dd-export-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-shrink: 0;
  white-space: nowrap;
  border-radius: 0.75rem;
  border: 1px solid rgba(16, 185, 129, 0.25);
  background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #052e1f;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.em-dd-export-btn:hover {
  border-color: rgba(16, 185, 129, 0.45);
  background: linear-gradient(to bottom, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.14));
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.em-dd-export-btn:disabled {
  pointer-events: none;
  opacity: 0.6;
}

.em-dd-export-btn__icon {
  font-size: 1.05rem;
  color: #059669;
  filter: drop-shadow(0 1px 0 rgba(0, 0, 0, 0.06));
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
